import { ChangeDetectionStrategy, Component, input, output, inject } from '@angular/core';
import { DocumentFile } from '@trade-platform/ui-shared';
import { Router } from '@angular/router';
import { fileType } from '@trade-platform/lib-enums';
import { getDocumentRouteId, isDisabledFile } from '../../../utils';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '../../../base.orders.store.facade';
import { AsyncPipe, NgClass } from '@angular/common';
import {
    AixDataTestingDirective,
    AixLoadingButtonComponent,
    AixTooltipDirective
} from '@trade-platform/ui-components';

@Component({
    selector: 'aix-document-list',
    templateUrl: 'document-list.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AixDataTestingDirective,
        NgClass,
        AixLoadingButtonComponent,
        AsyncPipe,
        AixTooltipDirective
    ]
})
export class AixDocumentListComponent {
    storeFacade = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    private router = inject(Router);

    documentsToDisplay = input<DocumentFile[]>();
    currentDocument = input<string>();

    // Offset to select items by index when we have multiple document lists
    offset = input<number>(0);

    clickDownload = output<string>();

    fileType = fileType;
    titleLoading: string;
    fileLoading: string;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    download(fileId: string) {
        this.clickDownload.emit(fileId);

        const document = this.documentsToDisplay()?.find(document => document.id === fileId);
        this.titleLoading = document?.screenName ?? document?.name ?? '';
        this.fileLoading = fileId;
    }

    isDisabledFile(file: DocumentFile) {
        return isDisabledFile(file);
    }

    /**
     * `currentDocument` is the route id, which carries the stable `formId` for a file that has one.
     * Comparing it against `id` alone would never light up the row for a form document. Matches
     * either identity, the same way the viewer resolves the route.
     *
     * refs trade-platform/trade-platform#7593
     */
    isActive(file: DocumentFile) {
        const current = this.currentDocument();
        if (!current) {
            return false;
        }
        return file.id === current || file.formId === current;
    }

    activateDocument(fileId: string) {
        const order = this.storeFacade.order;
        const document = order.files.find(file => file.id === fileId);
        if (!document || !this.isDisabledFile(document)) {
            // Keep the url on the stable form id so it stays valid if the file is replaced
            // while the viewer is open. refs #7593
            const routeId = getDocumentRouteId(order.files, fileId) ?? fileId;
            this.router.navigate(this.storeFacade.routes.documentViewer(order.id, routeId));
        }
    }
}
